<template>
  <div>
    <v-dialog width="80%" v-model="dialog" scrollable v-if="dialog">
      <v-card  class="invoice-show">
        <v-divider />

        <v-card-text class="pa-10">
          <v-row>
            <v-img src="~@images/logo.svg" max-width="250" contain />
            <v-spacer></v-spacer>
            <v-col cols="auto">
              <v-row dense>
                <v-col class="invoice-number text-right">
                  <v-tooltip bottom v-if="invoice.status === 'canceled'">
                    <template #activator="{ on, attrs }">
                      <v-chip small color="error" text-color="white" class="mr-3" v-bind="attrs" v-on="on">
                        {{ $t('invoice.statues.canceled') }}
                      </v-chip>
                    </template>
                    <span>{{ $t('invoice.canceled-banner') }}</span>
                  </v-tooltip>
                  <v-chip small class="mr-3" outlined v-if="invoice.emailDelivered">{{ $t('invoice.delivered-by-mail') }}</v-chip>
                  {{ $t('invoice.' + (isInvoice ? 'invoice-number' : 'invoice-credit-note-number'), {number: invoice.invoiceReference}) }}
                </v-col>
              </v-row>
              <v-row dense v-if="currentUser.isZE && invoice.sageInvoiceNumber">
                <v-col class="sage-invoice-number text-right">
                  {{ $t('invoice.sage-invoice-number-header', {number: invoice.sageInvoiceNumber}) }}
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-row class="mt-10">
            <v-col cols="auto" class="billing-request-address">
              <b class="name">{{ invoice.issuerName }}</b>
              <div v-if="invoice.issuerAddress" v-html="formatAddress(invoice.issuerAddress)"></div>
              <div>{{ $t('account-number') }} : <b>{{ invoice.issuerAccountNumber }}</b></div>
              <div v-if="invoice.issuerRegistrationNumber">{{ $t('organization.registration-number') }} : <b>{{ invoice.issuerRegistrationNumber }}</b></div>
              <div v-if="invoice.issuerVatNumber">{{ $t('vat-number') }} : <b>{{ invoice.issuerVatNumber }}</b></div>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="auto" class="invoice-address">
              <b class="name">{{ invoice.recipientName }}</b>
              <div v-if="invoice.recipientAddress" v-html="formatAddress(invoice.recipientAddress)"></div>
              <div>{{ $t('account-number') }} : <b>{{ invoice.recipientAccountNumber }}</b></div>
              <div v-if="invoice.organizationName">{{ $t('organization.organization') }} : <b>{{ invoice.organizationName }}</b></div>
              <div v-if="invoice.recipientRegistrationNumber">{{ $t('organization.registration-number') }} : <b>{{ invoice.recipientRegistrationNumber }}</b></div>
              <div v-if="invoice.recipientVatNumber">{{ $t('vat-number') }} : <b>{{ invoice.recipientVatNumber }}</b></div>
              <div v-if="invoice.recipientEmail">{{ $t('email') }} : <b>{{ invoice.recipientEmail }}</b></div>
              <div v-if="invoice.purchaseOrderNumber">{{ $t('invoice.purchase-order-number') }} : <b>{{ invoice.purchaseOrderNumber }}</b></div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="auto" class="invoice-infos">
              <div v-html="$t('invoice.period-from-to', {start: formatUtcDate(invoice.startDate, $t('format_date')), end: formatUtcDate(invoice.endDate, $t('format_date')) }) "></div>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="auto" class="text-right align-end d-flex">
              <div>{{ $t('created-at')}} <b>{{ invoice.createdAt | formatDate($t('format_date')) }}</b></div>
            </v-col>
          </v-row>

         <v-row v-for="section in sections" :key="section.label">
            <v-col>
              <h4 class="mb-1">{{ section.label }}</h4>
              <v-simple-table class="invoice-lines">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th width="100" class="text-center">{{ $t('invoice.product-code') }}</th>
                      <th>{{ $t('label') }}</th>
                      <th width="40" class="text-center">{{ $t('invoice.quantity') }}</th>
                      <th width="100" class="text-right">{{ $t('invoice.unit-price') }}</th>
                      <th width="100" class="text-right">{{ $t('invoice.vat-rate') }}</th>
                      <th width="100" class="text-center">{{ $t('country') }}</th>
                      <th width="150" class="text-right">{{ $t('invoice.total-ht') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(type, t) in section.types">
                      <tr :key="section.label + '::' + t">
                        <td colspan="7" class="invoice-line-type">{{ $t('invoice.line-types.' + type.label) }}</td>
                      </tr>
                      <tr v-for="line in type.lines" :key="line.id">
                        <td class="text-center">{{ line.productCode }}</td>
                        <td>
                          <div><b>{{ line.label }}</b></div>
                          <div v-for="(descLine, i) in line.description?.split('\n')" :key="line.id + '::' + i">{{ descLine }}</div>
                        </td>
                        <td class="text-center">{{ line.quantity }}</td>
                        <td class="text-right">{{ numberFormat(line.unitPrice) }} €</td>
                        <td class="text-right">{{ !line.isDisbursement ? (line.selfLiquidation ? $t('invoice.self-liquidation') : line.vatRate + ' %') : '' }}</td>
                        <td class="text-center">{{ !line.isDisbursement ? line.country : '' }}</td>
                        <td class="text-right ">{{ numberFormat(line.netTotal) }} €</td>
                      </tr>
                    </template>

                    <tr class="total">
                      <td colspan="5" class="no-border"></td>
                      <td class="text-center">{{ $t('invoice.total-ht') }}</td>
                      <td class="text-right font-weight-bold">{{ numberFormat(section.total) }} €</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-simple-table class="invoice-bases">
                <template v-slot:default>
                  <thead>
                  <tr>
                    <th width="100" class="text-right">{{ $t('invoice.base-ht') }}</th>
                    <th width="100" class="text-right">{{ $t('invoice.vat-rate') }}</th>
                    <th width="100" class="text-center">{{ $t('country') }}</th>
                    <th width="150" class="text-right">{{ $t('invoice.vat-amount') }}</th>
                  </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(base, k) in bases" :key="k">
                      <td class="text-right">{{ k === 'empty' ? ' ' : numberFormat(base.amount) + ' €'  }}</td>
                      <td class="text-right">{{ base.label }}</td>
                      <td class="text-center">{{ base.country }}</td>
                      <td class="text-right">{{ k === 'empty' ? ' ' :(base.selfLiquidation ? '-' : numberFormat(base.vatAmount) + ' €') }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
            <v-spacer></v-spacer>
            <v-col>
              <v-simple-table>
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <td width="150" class="text-right">{{ $t('amount-excl-vat') }}</td>
                      <td width="150" class="text-right">{{ numberFormat(invoice.netTotal) }} €</td>
                    </tr>
                    <tr>
                      <td class="text-right">{{ $t('vat') }}</td>
                      <td class="text-right">{{ numberFormat(invoice.vatTotal) }} €</td>
                    </tr>
                    <tr>
                      <td class="text-right font-weight-bold">{{ $t('amount-incl-vat') }}</td>
                      <td class="text-right font-weight-bold">{{ numberFormat(invoice.finalTotal) }} €</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>

          <!-- Payment Section (only for deferred customer invoices) -->
          <v-row v-if="showPaymentSection" class="mt-6">
            <v-col cols="12">
              <v-card outlined>
                <v-card-title class="py-2">
                  <v-icon left>mdi-credit-card-outline</v-icon>
                  {{ $t('invoice.payment') }}
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                  <v-row dense>
                    <v-col cols="4">
                      <div class="text-caption text--secondary">{{ $t('invoice.payment-status') }}</div>
                      <v-chip
                        small
                        :color="getPaymentStatusColor(invoice.paymentStatus)"
                        :dark="invoice.paymentStatus === 'paid' || invoice.paymentStatus === 'pending' || invoice.paymentStatus === 'failed'"
                      >
                        {{ getPaymentStatusLabel(invoice.paymentStatus) }}
                      </v-chip>
                    </v-col>
                    <template v-if="invoice.paymentStatus === 'paid'">
                      <v-col cols="4" v-if="invoice.paidWithPaymentMethodType">
                        <div class="text-caption text--secondary">{{ $t('invoice.payment-method') }}</div>
                        <div>{{ $t('invoice.payment-method-types.' + invoice.paidWithPaymentMethodType) }}</div>
                      </v-col>
                      <v-col cols="4" v-if="invoice.paidAt">
                        <div class="text-caption text--secondary">{{ $t('invoice.paid-at') }}</div>
                        <div>{{ invoice.paidAt | formatDate($t('format_date')) }}</div>
                      </v-col>
                      <v-col cols="4" v-if="invoice.paymentReference">
                        <div class="text-caption text--secondary">{{ $t('invoice.payment-reference') }}</div>
                        <div>{{ invoice.paymentReference }}</div>
                      </v-col>
                    </template>
                    <!-- Payment Attempts -->
                    <v-col cols="12" v-if="invoice.paymentAttempts && invoice.paymentAttempts.length">
                      <div class="text-caption text--secondary mt-2">{{ $t('invoice.payment-attempts') }}</div>
                      <v-simple-table dense class="mt-1">
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th>{{ $t('date') }}</th>
                              <th>{{ $t('invoice.payment-method') }}</th>
                              <th>{{ $t('status') }}</th>
                              <th>{{ $t('invoice.payment-error') }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(attempt, i) in invoice.paymentAttempts" :key="i">
                              <td>{{ attempt.attemptedAt | formatDate($t('format_datetime')) }}</td>
                              <td>{{ $t('invoice.payment-method-types.' + attempt.paymentMethodType) }}</td>
                              <td>
                                <v-chip
                                  x-small
                                  :color="attempt.status === 'success' ? 'green' : attempt.status === 'pending' ? 'orange' : 'red'"
                                  dark
                                >
                                  {{ $t('invoice.payment-attempt-status.' + attempt.status) }}
                                </v-chip>
                              </td>
                              <td>{{ attempt.errorMessage || '-' }}</td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-dialog max-width="400" v-model="sageInvoiceNumberDialog" scrollable>
            <v-form @submit.prevent="updateSageInvoiceNumber" v-if="invoice">
              <v-card>
                <v-card-text>
                  <v-row>
                    <v-col>
                      <v-text-field
                          :label="$t('invoice.sage-invoice-number')"
                          v-model="sageInvoiceNumber"
                          hide-details
                          clearable
                      />
                    </v-col>
                    <v-col cols="auto" class="pt-5">
                      <v-btn small type="submit" color="primary">
                        {{ $t('update') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-form>
          </v-dialog>

        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-row class="ma-0">
            <v-col cols="auto">
              <v-btn small @click="download" type="button"  :disabled="!invoice.storageFile">
                {{ $t('invoice.download-pdf') }}
              </v-btn>
              <v-btn class="ml-3" small @click="downloadAttachment" type="button" :disabled="!invoice.storageAttachment">
                {{ $t('invoice.download-attachments') }}
              </v-btn>
              <v-btn class="ml-3" small @click="openProforma" type="button" v-if="showOpenProformaButton">
                Ouvrir le proforma
              </v-btn>
              <v-btn class="ml-3" small @click="openSendMailDialog" type="button">
                <v-icon left small>mdi-email-outline</v-icon>
                {{ $t('invoice.send-mail-btn') }}
              </v-btn>
              <!-- Entrée unique « Créer un avoir » : ouvre le choix (avoir seul / annuler-recréer)
                   quand la facture y est éligible, sinon gère/affiche les avoirs existants. -->
              <v-btn class="ml-3" outlined small @click="handleCreditNotesAction" type="button" v-if="canCancelAndRecreate || hasCreditNotes">
                <v-icon left small>mdi-file-document-outline</v-icon>
                {{ creditNotesButtonLabel }}
              </v-btn>
              <v-btn class="ml-3" outlined small @click="openSourceInvoice" type="button" v-if="hasSourceInvoice">
                {{ $t('invoice.open-source-invoice') }}
              </v-btn>
              <v-btn class="ml-3" color="success" small @click="openMarkAsPaidDialog" type="button" v-if="canMarkAsPaid">
                <v-icon left small>mdi-check-circle</v-icon>
                {{ $t('invoice.mark-as-paid') }}
              </v-btn>
              <v-btn
                class="ml-3"
                :color="triggerPaymentColor"
                small
                :disabled="triggerPaymentDisabled"
                @click="confirmTriggerPayment"
                type="button"
                v-if="canTriggerPayment"
              >
                <v-icon left small>mdi-credit-card-outline</v-icon>
                {{ triggerPaymentLabel }}
              </v-btn>
            </v-col>
            <v-col class="text-right">
              <v-btn small class="primary" @click="dialog = false" type="button">
                {{ $t('close') }}
              </v-btn>
            </v-col>
          </v-row>

        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create Credit Note Confirmation Dialog -->
    <v-dialog v-model="showCreateCreditNoteDialog" max-width="520" persistent>
      <v-card>
        <v-card-text class="text-center pt-8 pb-4">
          <v-icon size="64" color="primary" class="mb-4">mdi-file-document-outline</v-icon>
          <h2 class="text-h6 mb-3">{{ $t('invoice.create-credit-note-title') }}</h2>
          <p class="text-body-2 grey--text text--darken-2 mb-2">
            {{ $t('invoice.create-credit-note-line1') }}
          </p>
          <p class="text-body-2 grey--text text--darken-2 mb-0">
            {{ $t('invoice.create-credit-note-line2') }}
          </p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn small text @click="showCreateCreditNoteDialog = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn small color="primary" depressed :loading="createCreditNoteLoading" @click="executeCreateCreditNote">
            <v-icon left small>mdi-file-document-outline</v-icon>
            {{ $t('invoice.create-credit-note') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Credit Note Choice Dialog -->
    <v-dialog v-model="showCreditNoteChoiceDialog" max-width="520">
      <v-card>
        <v-card-title class="text-h6">
          {{ $t('invoice.credit-note-choice-title') }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <v-card
            outlined
            class="mb-3 credit-note-choice-card"
            @click="chooseCreditNoteOnly"
          >
            <v-card-text class="d-flex align-center py-3">
              <v-icon color="primary" class="mr-3">mdi-file-document-outline</v-icon>
              <div>
                <div class="font-weight-bold">{{ $t('invoice.credit-note-choice-create') }}</div>
                <div class="text-caption grey--text">{{ $t('invoice.credit-note-choice-create-desc') }}</div>
              </div>
              <v-spacer />
              <v-icon small>mdi-chevron-right</v-icon>
            </v-card-text>
          </v-card>
          <v-card
            outlined
            class="credit-note-choice-card"
            @click="chooseCancelAndRecreate"
          >
            <v-card-text class="d-flex align-center py-3">
              <v-icon color="error" class="mr-3">mdi-refresh</v-icon>
              <div>
                <div class="font-weight-bold">{{ $t('invoice.credit-note-choice-cancel-recreate') }}</div>
                <div class="text-caption grey--text">{{ $t('invoice.credit-note-choice-cancel-recreate-desc') }}</div>
              </div>
              <v-spacer />
              <v-icon small>mdi-chevron-right</v-icon>
            </v-card-text>
          </v-card>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="showCreditNoteChoiceDialog = false">{{ $t('cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Send / Resend Mail Dialog -->
    <v-dialog v-model="showSendMailDialog" max-width="640">
      <v-card>
        <v-card-title class="text-h6">{{ $t('invoice.send-mail-title') }}</v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <div class="text-body-2 mb-1">
            {{ $t('invoice.send-mail-recipient') }} :
            <b>{{ mailHistory.recipientEmail || (invoice && invoice.recipientEmail) || '—' }}</b>
          </div>

          <!-- Aucune sélection par défaut : l'utilisateur DOIT choisir explicitement, ce qui
               fait office de confirmation et évite un envoi accidentel sur un misclic.
               La relance n'existe que pour les factures, jamais pour un avoir. -->
          <div class="text-subtitle-2 mb-1">{{ $t('invoice.send-mail-type-label') }}</div>
          <v-radio-group v-model="sendMailType" hide-details class="mt-0 mb-3">
            <v-radio value="invoice">
              <template #label>
                <div>
                  <div class="font-weight-bold">{{ $t('invoice.send-mail-type-normal') }}</div>
                  <div class="text-caption grey--text">{{ $t('invoice.send-mail-type-normal-desc') }}</div>
                </div>
              </template>
            </v-radio>
            <v-radio v-if="isInvoice" value="reminder" class="mt-2">
              <template #label>
                <div>
                  <div class="font-weight-bold">{{ $t('invoice.send-mail-type-reminder') }}</div>
                  <div class="text-caption grey--text">{{ $t('invoice.send-mail-type-reminder-desc') }}</div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>

          <v-divider class="mb-2" />
          <div class="text-subtitle-2 mb-1">{{ $t('invoice.send-mail-history') }}</div>
          <div v-if="sendMailHistoryLoading" class="text-caption grey--text py-2">{{ $t('invoice.send-mail-loading') }}</div>
          <div v-else-if="!mailHistory.sends.length" class="text-caption grey--text py-2">
            {{ $t('invoice.send-mail-history-empty') }}
          </div>
          <v-simple-table v-else dense>
            <template #default>
              <thead>
                <tr>
                  <th>{{ $t('invoice.send-mail-col-date') }}</th>
                  <th>{{ $t('invoice.send-mail-col-type') }}</th>
                  <th>{{ $t('invoice.send-mail-col-recipient') }}</th>
                  <th>{{ $t('invoice.send-mail-col-by') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(s, i) in mailHistory.sends" :key="i">
                  <td>
                    <span v-if="s.sentAt">{{ s.sentAt | formatDate($t('format_datetime')) }}</span>
                    <span v-else class="grey--text font-italic">{{ $t('invoice.send-mail-date-unknown') }}</span>
                  </td>
                  <td>
                    <v-chip x-small label :color="s.type === 'reminder' ? 'orange' : 'primary'" text-color="white">
                      {{ s.type === 'reminder' ? $t('invoice.send-mail-type-reminder') : $t('invoice.send-mail-type-normal') }}
                    </v-chip>
                    <span v-if="s.legacy" class="text-caption grey--text ml-1">{{ $t('invoice.send-mail-legacy') }}</span>
                  </td>
                  <td>{{ s.recipientEmail || '—' }}</td>
                  <td>{{ s.sentBy || '—' }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <div v-if="mailHistory.deliveryEvents && mailHistory.deliveryEvents.length" class="mt-3">
            <div class="text-subtitle-2 mb-1">{{ $t('invoice.send-mail-delivery') }}</div>
            <div v-for="(d, i) in mailHistory.deliveryEvents" :key="'d' + i" class="text-caption grey--text">
              {{ d.createdAt | formatDate($t('format_datetime')) }} —
              <span v-for="(e, j) in d.events" :key="j">{{ e.event }}<span v-if="j < d.events.length - 1">, </span></span>
            </div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn small text @click="showSendMailDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" depressed :loading="sendMailLoading" :disabled="!sendMailType" @click="confirmSendMail">
            <v-icon left small>mdi-send</v-icon>
            {{ sendMailType === 'reminder' ? $t('invoice.send-mail-confirm-reminder') : $t('invoice.send-mail-confirm-normal') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cancel & Recreate Confirmation Dialog -->
    <v-dialog v-model="showCancelRecreateDialog" max-width="520" persistent>
      <v-card>
        <v-card-text class="text-center pt-8 pb-4">
          <v-icon size="64" color="error" class="mb-4">mdi-alert-circle-outline</v-icon>
          <h2 class="text-h6 mb-3">{{ $t('invoice.cancel-and-recreate-title') }}</h2>
          <p class="text-body-2 grey--text text--darken-2 mb-2" v-html="$t('invoice.cancel-and-recreate-line1')"></p>
          <p class="text-body-2 grey--text text--darken-2 mb-2" v-html="$t('invoice.cancel-and-recreate-line2')"></p>
          <v-alert type="warning" text dense class="text-left mt-4 mb-0">
            {{ $t('invoice.cancel-and-recreate-warning') }}
          </v-alert>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn small text @click="showCancelRecreateDialog = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn small color="error" depressed :loading="cancelRecreateLoading" @click="executeCancelAndRecreate">
            <v-icon left small>mdi-alert</v-icon>
            {{ $t('invoice.cancel-and-recreate') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Credit Notes Dialog -->
    <v-dialog v-model="showCreditNotesDialog" max-width="900" scrollable>
      <v-card>
        <v-card-title>
          <span>{{ $t('invoice.credit-notes-list') }}</span>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-4">
          <v-data-table
            :headers="creditNotesHeaders"
            :items="invoice ? invoice.creditNotes : []"
            :items-per-page="10"
          >
            <template v-slot:item.invoiceReference="{ item }">
              <router-link :to="{query: {show: item.id}}" target="_blank">
                {{ item.invoiceReference }}
              </router-link>
            </template>

            <template v-slot:item.createdBy="{ item }">
              {{ item.createdBy ? `${item.createdBy.firstName} ${item.createdBy.lastName}` : '-' }}
            </template>

            <template v-slot:item.netTotal="{ item }">
              {{ item.netTotal }} €
            </template>

            <template v-slot:item.finalTotal="{ item }">
              {{ item.finalTotal }} €
            </template>

            <template v-slot:item.createdAt="{ item }">
              {{ item.createdAt | formatDate($t('format_date')) }}
            </template>
          </v-data-table>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-btn small @click="createCreditNoteFromDialog" v-if="canCreateCreditNote">
            {{ $t('invoice.create-new-credit-note') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn small @click="showCreditNotesDialog = false">
            {{ $t('close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Mark as Paid Dialog -->
    <v-dialog v-model="showMarkAsPaidDialog" max-width="500" persistent>
      <v-card>
        <v-card-title>
          {{ $t('invoice.mark-as-paid-title') }}
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-4">
          <v-form ref="markAsPaidForm">
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="markAsPaidForm.paymentMethodType"
                  :label="$t('invoice.payment-method')"
                  :items="paymentMethodTypeOptions"
                  outlined
                  dense
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <date-picker
                  v-model="markAsPaidForm.paidAt"
                  :label="$t('invoice.paid-at')"
                  clearable
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="markAsPaidForm.paymentReference"
                  :label="$t('invoice.payment-reference')"
                  outlined
                  dense
                  :placeholder="$t('invoice.payment-reference')"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn small text @click="showMarkAsPaidDialog = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn small color="success" @click="submitMarkAsPaid">
            {{ $t('confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import InvoiceRepository from "@repository/InvoiceRepository";
import ProformaInvoiceRepository from "@repository/ProformaInvoiceRepository";

export default {

  components: {

  },

  data() {
    return {
      invoiceId: null,
      dialog: false,
      sageInvoiceNumberDialog: false,
      sageInvoiceNumber: null,
      invoice: null,
      showCreditNotesDialog: false,
      showMarkAsPaidDialog: false,
      markAsPaidForm: {
        paymentMethodType: 'transfer',
        paidAt: null,
        paymentReference: '',
      },
      triggerPaymentLoading: false,
      showCreateCreditNoteDialog: false,
      createCreditNoteLoading: false,
      showCreditNoteChoiceDialog: false,
      showCancelRecreateDialog: false,
      cancelRecreateLoading: false,
      showSendMailDialog: false,
      sendMailLoading: false,
      sendMailHistoryLoading: false,
      sendMailType: null,
      mailHistory: { sends: [], deliveryEvents: [], hasBeenSent: false, reminderCount: 0, recipientEmail: null },
    }
  },

  props: {
    fromProformaInvoice: {
      type: Boolean,
      default: false
    },
  },

  watch: {

  },

  computed: {
    sections() {
      const sections = {}
      for(let item of this.invoice.lines) {
        if(!sections[item.section]) {
          sections[item.section] = {
            label: item.section,
            types: {},
            total: 0,
          }
        }
        if(!sections[item.section].types[item.type]) {
          sections[item.section].types[item.type] = {
            label: item.type,
            lines: [],
            total: 0
          }
        }
        sections[item.section].total += item.netTotal
        sections[item.section].types[item.type].lines.push(item)
        sections[item.section].types[item.type].total += item.netTotal
      }


      const finalSections = []
      for(let section in sections) {

        const newSection = {
          label: sections[section].label,
          types: [],
          total: sections[section].total
        }

        for(const type in sections[section].types) {
          newSection.types.push(sections[section].types[type])
        }

        finalSections.push(newSection)
      }

      return finalSections
    },

    bases() {
      const bases = {}

      for(let item of this.invoice.lines) {
        if(item.isDisbursement) {
          continue
        }

        let b = item.vatRate
        if(item.selfLiquidation) {
          b = 'auto'
        }
        b += '_' + item.country

        if(!bases[b]) {
          bases[b] = {
            label: item.selfLiquidation ? this.$t('invoice.self-liquidation') : item.vatRate + '%',
            country: item.country,
            amount: 0,
            vatAmount: 0,
            selfLiquidation: item.selfLiquidation,
          }
        }
        bases[b].amount += item.netTotal
      }

      for(let b in bases) {
        let item = bases[b]
        bases[b].vatAmount = item.amount * (item.selfLiquidation ? 0 : item.label.replace('%', '') / 100)
      }

      if(Object.keys(bases).length === 0) {
        return {
          "empty": {

          }
        }
      }

      return bases
    },

    isInvoice() {
      return this.invoice?.documentKind === 'invoice'
    },

    showOpenProformaButton() {
      return this.invoice?.proformaInvoiceId && this.currentUser.isZE && !this.fromProformaInvoice
    },

    canCreateCreditNote() {
      if(!this.invoice) return false

      if(this.invoice.documentKind !== 'invoice') {
        return false
      }

      if(this.invoice.recipientType === 'customer') {
        return this.currentUser.permissions.includes('INVOICE_CUSTOMER_WRITE')
      }

      return this.currentUser.permissions.includes('INVOICE_WRITE')
    },

    hasCreditNotes() {
      return this.invoice && this.invoice.creditNotes && this.invoice.creditNotes.length > 0
    },

    hasSourceInvoice() {
      return this.invoice && this.invoice.documentKind === 'credit-note' && this.invoice.invoiceSource
    },

    creditNotesHeaders() {
      return [
        { text: this.$t('created-at'), value: 'createdAt', sortable: true },
        { text: this.$t('invoice.invoice-reference'), value: 'invoiceReference', sortable: true },
        { text: this.$t('amount-excl-vat'), value: 'netTotal', sortable: true },
        { text: this.$t('created-by'), value: 'createdBy', sortable: false },
      ]
    },

    creditNotesButtonLabel() {
      if (this.hasCreditNotes) {
        return this.$t('invoice.credit-notes-manage', {count: this.invoice.creditNotes.length})
      }
      return this.$t('invoice.create-credit-note')
    },

    showPaymentSection() {
      return this.invoice && this.invoice.paymentStatus != null
    },

    canCancelAndRecreate() {
      // Same visibility rules as canCreateCreditNote + status check
      if (!this.canCreateCreditNote) return false
      if (!['issued', 'paid'].includes(this.invoice.status)) return false
      if (this.invoice.creditNotes?.length > 0) return false
      return true
    },

    canMarkAsPaid() {
      return this.invoice && this.invoice.paymentStatus !== 'paid' && this.currentUser.permissions.includes('INVOICE_WRITE')
    },

    canTriggerPayment() {
      return this.invoice?.isPayable === true
        && this.currentUser.permissions.includes('INVOICE_WRITE')
    },

    triggerPaymentLabel() {
      if (this.triggerPaymentLoading) return this.$t('invoice.trigger-payment-pending')
      if (this.invoice?.paymentStatus === 'failed') return this.$t('invoice.trigger-payment-retry')
      return this.$t('invoice.trigger-payment')
    },

    triggerPaymentColor() {
      if (this.invoice?.paymentStatus === 'failed') return 'warning'
      return 'primary'
    },

    triggerPaymentDisabled() {
      return this.triggerPaymentLoading
    },

    paymentMethodTypeOptions() {
      return [
        {text: this.$t('invoice.payment-method-types.transfer'), value: 'transfer'},
        // {text: this.$t('invoice.payment-method-types.sepa_debit'), value: 'sepa_debit'},
        // {text: this.$t('invoice.payment-method-types.card'), value: 'card'},
      ]
    },

  },


  methods: {

    open(invoiceId) {
      this.invoiceId = invoiceId
      this.showLoading(true)
      this.loadData().then(() => {
        this.dialog = true
      })
    },

    loadData() {
      return new Promise((resolve, reject) => {
        this.showLoading(true)
        InvoiceRepository.get(this.invoiceId).then(invoice => {
          this.invoice = invoice
          this.sageInvoiceNumber = invoice.sageInvoiceNumber
          this.showLoading(false)
          resolve(invoice)
        }).catch(error => {
          this.notifyError(error)
          this.showLoading(false)
        })
      })
    },

    formatAddress(address) {
      if(!address) {
        return ''
      }

      const lines = []

      let str = ''
      if(address.number) {
        str += address.number + ', '
      }
      if(address.street) {
        str += address.street
      }
      if(str) {
        lines.push(str)
      }

      str = ''
      if(address.zipcode) {
        str += address.zipcode + ' '
      }
      if(address.city) {
        str += address.city
      }
      if(str) {
        lines.push(str)
      }

      if(address.country) {
        lines.push(this.countryCodeToText(address.country))
      }
      return lines.join('<br>')
    },

    download() {
      this.showLoading(true)
      InvoiceRepository.pdf(this.invoice.id).then(() => {
        this.showLoading(false)
      }).catch(error => {
        this.notifyError(error)
        this.showLoading(false)
      })
    },

    downloadAttachment() {
      this.showLoading(true)
      InvoiceRepository.downloadAttachment(this.invoice.id).then(url => {
        this.showLoading(false)
      }).catch(error => {
        this.notifyError(error)
        this.showLoading(false)
      })
    },


    updateSageInvoiceNumber() {
      this.showLoading(true)
      InvoiceRepository.updateSageInvoiceNumber(this.invoice.id, this.sageInvoiceNumber).then(() => {
          this.notifySuccess(this.$t('update-success'));
          this.sageInvoiceNumberDialog = false
          this.invoice.sageInvoiceNumber = this.sageInvoiceNumber
          this.showLoading(false)
      }).catch(error => {
          this.notifyError(error)
          this.showLoading(false)
      })
    },

    openProforma() {
      this.$router.push({ name: 'proforma-invoices.edit', params: {id: this.invoice.proformaInvoiceId }})
    },

    openSendMailDialog() {
      // No pre-selection on purpose: the user MUST pick the type explicitly, which acts as a
      // safeguard against accidental sends (a misclick should never fire a mail to a client).
      this.sendMailType = null
      this.mailHistory = { sends: [], deliveryEvents: [], hasBeenSent: false, reminderCount: 0, recipientEmail: null }
      this.showSendMailDialog = true
      this.sendMailHistoryLoading = true
      InvoiceRepository.mailHistory(this.invoice.id).then(history => {
        this.mailHistory = history
      }).catch(() => {
        // History is best-effort; the dialog still works without it.
      }).finally(() => {
        this.sendMailHistoryLoading = false
      })
    },

    confirmSendMail() {
      if (!this.sendMailType) return
      this.sendMailLoading = true
      InvoiceRepository.sendMail(this.invoice.id, this.sendMailType).then(() => {
        this.sendMailLoading = false
        this.showSendMailDialog = false
        this.notifySuccess(this.$t(this.sendMailType === 'reminder' ? 'invoice.reminder-sent' : 'billing-request.invoice-sent'))
        this.loadData()
      }).catch(error => {
        this.sendMailLoading = false
        this.notifyError(error)
      })
    },

    createCreditNote() {
      if(this.canCreateCreditNote) {
        this.showCreateCreditNoteDialog = true
      } else {
        this.notifyError(this.$t('invoice.cannot-create-credit-note'))
      }
    },

    openSourceInvoice() {
      if(this.hasSourceInvoice) {
        // Open the source invoice in the same modal
        const r = this.$router.resolve({name: 'invoices', query: {show: this.invoice.invoiceSource.id}})
        window.open(r.href)
      }
    },

    handleCreditNotesAction() {
      // If there are existing credit notes, show the list dialog
      if(this.hasCreditNotes) {
        this.showCreditNotesDialog = true
      }
      // If eligible for cancel-and-recreate, show the choice dialog
      else if(this.canCancelAndRecreate) {
        this.showCreditNoteChoiceDialog = true
      }
      // Otherwise, directly create a credit note (no cancel-and-recreate option)
      else if(this.canCreateCreditNote) {
        this.createCreditNote()
      }
    },

    createCreditNoteFromDialog() {
      // Close the dialog first
      this.showCreditNotesDialog = false
      // Then create the credit note
      this.$nextTick(() => {
        this.createCreditNote()
      })
    },

    getPaymentStatusColor(status) {
      switch (status) {
        case 'paid':
          return 'green'
        case 'pending':
          return 'orange'
        case 'failed':
          return 'red'
        default:
          return 'grey'
      }
    },

    getPaymentStatusLabel(status) {
      if (status) {
        return this.$t('invoice.payment-statuses.' + status)
      }
      return this.$t('invoice.payment-statuses.not-processed')
    },

    openMarkAsPaidDialog() {
      this.markAsPaidForm = {
        paymentMethodType: 'transfer',
        paidAt: null,
        paymentReference: '',
      }
      this.showMarkAsPaidDialog = true
    },

    submitMarkAsPaid() {
      this.showLoading(true)
      const data = {
        paymentMethodType: this.markAsPaidForm.paymentMethodType,
      }
      if (this.markAsPaidForm.paidAt) {
        data.paidAt = this.markAsPaidForm.paidAt
      }
      if (this.markAsPaidForm.paymentReference) {
        data.paymentReference = this.markAsPaidForm.paymentReference
      }

      InvoiceRepository.markPaid(this.invoice.id, data).then(() => {
        this.showLoading(false)
        this.showMarkAsPaidDialog = false
        this.$dialog.notify.success(this.$t('invoice.mark-as-paid-success'))
        this.loadData()
        this.$emit('updated')
      }).catch(error => {
        this.showLoading(false)
        this.notifyError(error)
      })
    },

    chooseCreditNoteOnly() {
      this.showCreditNoteChoiceDialog = false
      this.$nextTick(() => this.createCreditNote())
    },

    executeCreateCreditNote() {
      this.createCreditNoteLoading = true
      ProformaInvoiceRepository.createCreditNoteFromInvoice(this.invoice.id).then((result) => {
        this.createCreditNoteLoading = false
        this.showCreateCreditNoteDialog = false
        const route = this.$router.resolve({name: 'proforma-invoices.edit', params: {id: result.id}})
        window.open(route.href)
      }).catch(error => {
        this.createCreditNoteLoading = false
        this.notifyError(error)
      })
    },

    chooseCancelAndRecreate() {
      this.showCreditNoteChoiceDialog = false
      this.$nextTick(() => { this.showCancelRecreateDialog = true })
    },

    confirmCancelAndRecreate() {
      this.showCancelRecreateDialog = true
    },

    executeCancelAndRecreate() {
      this.cancelRecreateLoading = true
      InvoiceRepository.cancelAndRecreate(this.invoice.id).then(result => {
        this.cancelRecreateLoading = false
        this.showCancelRecreateDialog = false
        this.loadData()
        this.$emit('cancel-and-recreate', result)
        // Ask the user if they want to open the new proforma
        if (result.newProformaInvoiceId) {
          this.$dialog.confirm({
            text: this.$t('invoice.cancel-and-recreate-open-proforma'),
          }).then(confirm => {
            if (confirm) {
              const route = this.$router.resolve({name: 'proforma-invoices.edit', params: {id: result.newProformaInvoiceId}})
              window.open(route.href)
            }
          })
        } else {
          this.$dialog.notify.success(this.$t('invoice.cancel-and-recreate-success'))
        }
      }).catch(error => {
        this.cancelRecreateLoading = false
        this.notifyError(error)
      })
    },

    confirmTriggerPayment() {
      const amount = this.numberFormat(this.invoice.finalTotal) + ' €'
      let text = this.$t('invoice.trigger-payment-confirm', { amount })
      if (this.invoice.paymentWarning === 'no-collection-method') {
        text += '\n\n⚠️ ' + this.$t('invoice.trigger-payment-warn-no-collection-method')
      } else if (this.invoice.paymentWarning === 'collection-method-is-bank-transfer') {
        text += '\n\n⚠️ ' + this.$t('invoice.trigger-payment-warn-bank-transfer')
      }
      this.$dialog.confirm({ text }).then(confirm => {
        if (confirm) this.triggerPayment()
      })
    },

    triggerPayment() {
      this.triggerPaymentLoading = true
      this.showLoading(true)
      InvoiceRepository.triggerPayment(this.invoice.id)
        .then(() => {
          this.triggerPaymentLoading = false
          this.showLoading(false)
          this.$dialog.notify.success(this.$t('invoice.trigger-payment-success'))
          this.loadData()
          this.$emit('updated')
        })
        .catch(error => {
          this.triggerPaymentLoading = false
          this.showLoading(false)
          const code = error?.response?.data?.message
          if (code === 'invoice.trigger-payment.already-paid') {
            this.$dialog.notify.info(this.$t('invoice.trigger-payment-already-paid'))
          } else if (code === 'invoice.trigger-payment.unsupported-type') {
            this.$dialog.notify.warning(this.$t('invoice.trigger-payment-unsupported-type'))
          } else if (code === 'invoice.trigger-payment.not-deferred') {
            this.$dialog.notify.warning(this.$t('invoice.trigger-payment-not-deferred'))
          } else if (code === 'invoice.trigger-payment.not-auto-debit') {
            this.$dialog.notify.warning(this.$t('invoice.trigger-payment-not-auto-debit'))
          } else if (code === 'invoice.trigger-payment.no-payment-method') {
            this.$dialog.notify.warning(this.$t('invoice.trigger-payment-no-payment-method'))
          } else if (code === 'invoice.trigger-payment.failed') {
            this.$dialog.notify.error(this.$t('invoice.trigger-payment-failed'))
          } else {
            this.notifyError(error)
          }
          this.loadData()
        })
    },
  }
}
</script>

<style lang="scss">
.invoice-show {
  .invoice-number {
    font-size:20px;
    font-weight: bold;
  }

  .invoice-address {
    min-width: 400px;
  }

  .v-data-table {
    tr {
      &:hover {
        background-color: transparent !important;
      }
      th {
        border-top: 1px solid #e0e0e0 !important;
        border-left: 1px solid #e0e0e0 !important;
        &:last-child {
          border-right: 1px solid #e0e0e0 !important;
        }
      }
      td {
        border-top: 1px solid #e0e0e0 !important;
        border-left: 1px solid #e0e0e0 !important;
        height: 30px !important;
        &.invoice-line-type {
          text-align: center;
          background: #eee;
        }
        &.no-border {
          border-left: 0 !important;
        }
        &:last-child {
          border-right: 1px solid #e0e0e0 !important;
        }
      }
      &:last-child {
        td:not(.no-border) {
          border-bottom: 1px solid #e0e0e0 !important;
        }
      }
    }
  }
}
</style>
